import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Navbar from "../components/Navbar";
import PendingFeePopup from "../components/PendingFeePopup";
import AccessDenied from "../components/AccessDenied";
import LoginRequired from "../components/LoginRequired";
import Loader from "../components/Loader";
import { useViewPermission } from "../context/PermissionContext";
import { resolveStudentFeeState } from "../utils/studentFeeState";
import * as registrationsApi from "../api/registrations";
import "../screens/MyCourses.css";

const ConfirmCheckout = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth0();
  const { hasPermission, loading: permLoading } =
    useViewPermission("view_my_courses");
  const [regObj, setRegObj] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (authLoading || !isAuthenticated || permLoading || !hasPermission) {
      return;
    }
    if (!user?.email) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      setLoadError(null);
      try {
        const myCoursesRes = await registrationsApi.myCourses({
          user: user.email,
        });
        if (!cancelled) {
          setRegObj(myCoursesRes.data?.[0] || null);
        }
      } catch (err) {
        console.error("Error loading checkout data:", err);
        if (!cancelled) {
          setLoadError("Failed to load checkout data.");
          setRegObj(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [
    authLoading,
    isAuthenticated,
    permLoading,
    hasPermission,
    user?.email,
  ]);

  if (authLoading || (isAuthenticated && permLoading)) {
    return (
      <>
        <Navbar />
        <div className="mycourses-page mycourses-page--loading">
          <div className="mycourses-page__inner">
            <div className="mycourses-loading" aria-live="polite" aria-busy="true">
              <Loader />
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="text-center">
        <Navbar />
        <LoginRequired message="Please sign in to view checkout and pending fee details." />
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  const feeState = regObj ? resolveStudentFeeState(regObj) : null;
  const goMyCourses = () => navigate("/mycourses");

  return (
    <>
      <Navbar />
      <div className="mycourses-page">
        <div className="mycourses-page__inner">
          {loading ? (
            <div className="mycourses-loading" aria-live="polite" aria-busy="true">
              <Loader />
            </div>
          ) : loadError ? (
            <div className="mycourses-error" role="alert">
              <h2 className="mycourses-error__title">Something went wrong</h2>
              <p className="mycourses-error__copy">{loadError}</p>
              <Link to="/mycourses" className="mycourses-error__cta">
                Back to My Courses
              </Link>
            </div>
          ) : !regObj ? (
            <div className="mycourses-empty">
              <h2 className="mycourses-empty__title">No registration found</h2>
              <p className="mycourses-empty__copy">
                We could not find a registration for checkout.
              </p>
              <Link to="/mycourses" className="mycourses-empty__cta">
                Back to My Courses
              </Link>
            </div>
          ) : feeState?.needsPayment ? (
            <PendingFeePopup
              regObj={regObj}
              onClose={goMyCourses}
              onResolved={goMyCourses}
            />
          ) : (
            <div className="mycourses-empty">
              <h2 className="mycourses-empty__title">No payment due</h2>
              <p className="mycourses-empty__copy">
                Your payment status is {feeState?.paymentStatusLabel || "Paid"}{" "}
                with {feeState?.outstandingLabel || "PKR 0"} outstanding.
              </p>
              <Link to="/mycourses" className="mycourses-empty__cta">
                Back to My Courses
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ConfirmCheckout;
